import React, { useEffect } from "react"
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import config from "aldoo-ra/Config"
import Value from "aldoo-ra/Value"
import AldooStoreAPI from "aldoo-ra/Store/api"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { store } from "aldoo-ra/Store"

const { STRIPE_API_KEY } = config
// Lazy singleton: Stripe.js downloads on first render of this element,
// not at module import (store elements are eagerly bundled on every page)
let _stripePromise = null
const stripePromise = () => (_stripePromise ??= loadStripe(STRIPE_API_KEY))
let setupLoading = false

// The inner form component that uses Stripe hooks
const PaymentMethodForm = ({ onSuccess, onCancel, paymentMethods, clearSetupIntent }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [ready, setReady] = useState(false)
  const [makeDefault, setMakeDefault] = useState(false)
  const [error, setError] = useState(null)
  const [agreed, setAgreed] = useState(false)

  const texts = TextBundle({ id: "aldoo-store" })

  const isReady = stripe && ready && !loading

  // Function to check if a payment method already exists
  const isDuplicateMethod = (newMethod) => {
    return paymentMethods.some((method) => {
      // Check if both methods are of type 'card' and have the necessary card details
      if (method.type === "card" && newMethod?.card) {
        return (
          method.card?.last4 === newMethod.card?.last4 &&
          method.card?.exp_month === newMethod.card?.exp_month &&
          method.card?.exp_year === newMethod.card?.exp_year
        )
      }
      // Add checks for other types of payment methods if needed
      return false
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (loading) return
    if (!stripe || !elements) return

    setLoading(true)
    setError(null)

    try {
      // Validate the form
      const { error: submitError } = await elements.submit()
      if (submitError) {
        throw submitError
      }

      // Confirm the setup intent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {},
      })

      if (confirmError) {
        throw confirmError
      }

      // Retrieve the setup intent details
      const { setupIntent: setupIntentDetails } =
        await stripe.retrieveSetupIntent(setupIntent.client_secret)

      if (!setupIntentDetails || !setupIntentDetails.payment_method) {
        throw new Error(texts?.payment_method_retrieval_fail || "Failed to retrieve payment method details.")
      }

      // Check if payment_method_details exists and contains card details
      const paymentMethodDetails =
        setupIntentDetails.payment_method_details || {}
      const paymentMethodType = paymentMethodDetails.type || "unknown"
      const paymentMethodCard = paymentMethodDetails.card || null

      const paymentMethod = {
        id: setupIntentDetails.payment_method,
        type: paymentMethodType,
        card: paymentMethodCard,
      }

      // Check for duplicate payment method
      if (isDuplicateMethod(paymentMethod)) {
        setError(texts?.payment_method_duplicate || "This method is already added to your account.")
        return
      }

      // Attach the payment method to the user's account
      const response = await AldooStoreAPI.attachPaymentMethod({
        payment_method_id: paymentMethod.id,
        makeDefault,
      })

      if (response.error) {
        throw new Error(response.error)
      }

      // Clear setupIntent immediately to prevent reuse
      clearSetupIntent?.()

      window.dispatchEvent(new CustomEvent("payment-methods-updated"))
      onSuccess?.(paymentMethod)
    } catch (err) {
      setError(err.message)
      store.logPaymentError(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="w-full max-w-lg">
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/30 text-error rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mt-2 mb-0 min-h-[430px]">
          <PaymentElement
            onReady={() => setReady(true)}
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
            }}
          />
        </div>

        <div className="flex items-center space-x-3 mb-4">
          <ToggleSwitch
            isOn={makeDefault}
            onChange={setMakeDefault}
          />

          <Typography
            variant="info_14"
            className="text-text my-0"
          >
            {texts?.make_payment_method_default || "Make this my default payment method"}
          </Typography>
        </div>

        <div className="flex justify-end space-x-3">
          {onCancel && (
            <Button
              variant="secondary_s"
              onClick={onCancel}
            >
              {texts?.cta_add_payment_method_cancel || "Cancel"}
            </Button>
          )}

          <Button
            type="submit"
            disabled={!isReady || loading}
            variant="primary_s"
            className={`
              ${
                isReady && !loading
                  ? "bg-primary hover:bg-primary_hov"
                  : "bg-gray_3 cursor-not-allowed"
              }
              transition-colors`}
          >
            {loading ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>{texts?.processing_add_payment_method || "Processing..."}</span>
              </span>
            ) : (
              texts?.cta_add_payment_method || "Add Payment Method"
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}

// The main component that handles setup and modal
const AddPaymentMethod = ({ isOpen, onClose, paymentMethods }) => {
  const texts = TextBundle({ id: "aldoo-store" })
  const [setupIntent, setSetupIntent] = Value({
    key: "payment-setup-intent",
    defaultValue: null,
  })
  const { getFragment } = store

  const PreparingPayment = getFragment("checkout-preparing-payment")
  const PaymentContainer = getFragment("checkout-container")

  useEffect(() => {
    const initializeSetup = async () => {
      if (setupIntent || setupLoading) return
      setupLoading = true

      try {
        const response = await AldooStoreAPI.initializePaymentMethodSetup()
        setupLoading = false

        if (response.error) {
          console.error("Failed to initialize setup:", response.error)
          return
        }

        setSetupIntent(response)
      } catch (error) {
        console.error("Failed to initialize setup:", error)
        setupLoading = false
      }
    }

    if (isOpen) initializeSetup()
  }, [isOpen])

  if (!isOpen) return null

  return (
    <Modal
      title={setupIntent && texts?.addPaymentMethod}
      isOpen={isOpen}
      variant="aqua_big"
      className="md:max-w-[600px] min-h-[500px]"
      onClose={onClose}
      styleOverrides={{
        container:
        "w-full md:w-[755px] h-full md:h-auto md:max-h-[calc(100vh-1rem)] bg-paper overflow-hidden md:rounded-[24px] pt-6 pb-6",
        closeButton: "absolute right-4 md:right-5 top-0 z-10",
      }}
    >
      {!setupIntent && <PreparingPayment />}
      {setupIntent && setupIntent.clientSecret && (
        <PaymentContainer store={store}>
          <Elements
            stripe={stripePromise()}
            options={{
              clientSecret: setupIntent.clientSecret,
              loader: "always",

              appearance: {
                variables: {
                  colorDanger: "#df1b41",
                  colorBackground: "#f9f9f9",
                  colorText: "#090909",
                },
              },
            }}
          >
            <PaymentMethodForm
              paymentMethods={paymentMethods}
              clearSetupIntent={() => setSetupIntent(null)}
              onSuccess={() => {
                onClose()
                setSetupIntent(null)
              }}
              onCancel={onClose}
            />
          </Elements>
        </PaymentContainer>
      )}
    </Modal>
  )
}

export default AddPaymentMethod
